import React, { useState, useEffect } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { Toaster, toast } from "react-hot-toast";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";

function GenericFAQAdd() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const typeParam = queryParams.get("type");

  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    setValue,
    reset,
  } = useForm({
    defaultValues: {
      faqs: [{ question: "", answer: "" }],
      for: "",
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "faqs",
  });

  const [isLoading, setIsLoading] = useState(false);

  // Fetch FAQ data when editing
  useEffect(() => {
    const fetchFAQsData = async () => {
      if (id) {
        try {
          const token = localStorage.getItem("astroVani-admin-token");
          const endpoint = typeParam === "pooja" 
            ? `${process.env.REACT_APP_BASEURL}/admin/pooja/faq/${id}`
            : `${process.env.REACT_APP_BASEURL}/admin/faq/${id}`;
            
          const response = await axios.get(
            endpoint,
            {
              headers: { Authorization: token },
            }
          );

          const faqData = response?.data?.data;
          if (faqData) {
            reset({
              faqs: typeParam === "pooja" 
                ? [{ question: faqData.question, answer: faqData.answer, _id: faqData._id }]
                : (faqData?.faqEntries?.length ? faqData.faqEntries : [{ question: "", answer: "" }]),
              for: typeParam === "pooja" ? "pooja" : (faqData?.for || ""),
            });
          }
        } catch (error) {
          console.error("Error fetching FAQ data:", error);
        }
      }
    };

    fetchFAQsData();
  }, [id, reset]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const sanitizedFAQs = data.faqs.map(({ _id, ...rest }) => rest);

      const endpointPatch = data.for === "pooja"
        ? `${process.env.REACT_APP_BASEURL}/admin/pooja/faq/${id}`
        : `${process.env.REACT_APP_BASEURL}/admin/faq/${id}`;
        
      const endpointPost = data.for === "pooja"
        ? `${process.env.REACT_APP_BASEURL}/admin/pooja/faq`
        : `${process.env.REACT_APP_BASEURL}/admin/faq`;

      const headers = {
        "Content-Type": "application/json",
        Authorization: `${token}`,
      };

      let response;
      if (id) {
        const formattedData = data.for === "pooja"
          ? { question: data.faqs[0].question, answer: data.faqs[0].answer, isActive: true }
          : { faqEntries: sanitizedFAQs, for: data.for };
        response = await axios.patch(endpointPatch, formattedData, { headers });
      } else {
        if (data.for === "pooja") {
          const promises = data.faqs.map(faq => 
            axios.post(endpointPost, { question: faq.question, answer: faq.answer, isActive: true }, { headers })
          );
          const responses = await Promise.all(promises);
          response = responses[0];
        } else {
          const formattedData = { faqEntries: sanitizedFAQs, for: data.for };
          response = await axios.post(endpointPost, formattedData, { headers });
        }
      }

      if (response?.data?.success) {
        toast.success(id ? "FAQ updated successfully" : "FAQs saved successfully");
        navigate("/generic-faq");
      } else {
        toast.error("FAQ not saved");
      }
    } catch (error) {
      console.error("Error saving FAQ:", error);
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Toaster position="top-right" reverseOrder={true} />
      <div className="card-body">
        <button
          onClick={() => window.history.back()}
          style={{
            marginBottom: "10px",
            backgroundColor: "transparent",
            border: "none",
            cursor: "pointer",
            fontFamily: "monospace",
            fontSize: "18px",
            color: "#007bff",
            display: "flex",
            alignItems: "center",
            position: "relative",
            padding: "10px 20px",
          }}
        >
          <span style={{ marginRight: "8px" }}>&lt;</span>
          <span style={{ position: "relative", display: "inline-block" }}>
            Back
            <span
              style={{
                content: "''",
                position: "absolute",
                left: 0,
                bottom: -2,
                width: "100%",
                height: "1px",
                borderBottom: "2px solid #007bff",
              }}
            ></span>
          </span>
        </button>

        <h2>{id ? "Edit Generic FAQ" : "Add Generic FAQ"}</h2>
        <form className="row g-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="col-md-6">
            <label className="form-label">FAQ For</label>
            <select
              className={`form-select ${errors.for ? "is-invalid" : ""}`}
              {...register("for", {
                required: "Please select who this FAQ is for.",
                validate: (value) =>
                  value === "user" || value === "astro" || value === "pooja" || "Invalid selection",
              })}
              disabled={!!id}
            >
              <option value="">Select</option>
              <option value="user">User</option>
              <option value="astro">Astro</option>
              <option value="pooja">Pooja</option>
            </select>
            {errors.for && (
              <div className="invalid-feedback">{errors.for.message}</div>
            )}
          </div>

          <div className="col-md-12">
            {fields.map((item, index) => (
              <div key={item.id} className="mb-4 border rounded p-3">
                <label className="form-label">Question</label>
                <textarea
                  className="form-control"
                  rows="3"
                  placeholder="Enter your question"
                  {...register(`faqs[${index}].question`, {
                    required: "Question is required",
                  })}
                />
                {errors.faqs?.[index]?.question && (
                  <span className="text-danger">
                    {errors.faqs[index].question.message}
                  </span>
                )}

                <label className="form-label mt-3">Answer</label>
                <textarea
                  className="form-control"
                  rows="5"
                  placeholder="Enter your answer"
                  {...register(`faqs[${index}].answer`, {
                    required: "Answer is required",
                  })}
                />
                {errors.faqs?.[index]?.answer && (
                  <span className="text-danger">
                    {errors.faqs[index].answer.message}
                  </span>
                )}

                {fields.length > 1 && !id && (
                  <button
                    type="button"
                    className="btn btn-danger mt-2"
                    onClick={() => remove(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            {!id && (
              <button
                type="button"
                className="btn btn-primary mt-2"
                onClick={() => append({ question: "", answer: "" })}
              >
                Add FAQ
              </button>
            )}
          </div>

          <div className="col-12 mt-4">
            <button
              type="submit"
              className="btn btn-primary text-uppercase px-3"
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

export default GenericFAQAdd;
